import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

const CONTACT_EMAIL = "azizmashkour@gmail.com";

const WUSC_URL = "https://wusc.ca/fr/";
const WUSC_PRIVACY_URL = "https://wusc.ca/fr/confidentialite/";
const CECI_URL = "https://www.ceci.ca/fr/";

/** Shared anchor props for the outbound partner links. */
const externalLink = (href: string) => (
  <a className="nbs-legal__mail" href={href} target="_blank" rel="noopener noreferrer" />
);

/**
 * Privacy + terms page. The platform is delivered by EUMC/WUSC and CECI, so
 * both are named here and the detailed handling of personal information points
 * back to the EUMC privacy policy the project operates under.
 */
export function LegalPage() {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (location.hash !== "#conditions") return;
    document.getElementById("conditions")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash]);

  const partnerComponents = {
    wusc: externalLink(WUSC_URL),
    ceci: externalLink(CECI_URL),
    policy: externalLink(WUSC_PRIVACY_URL),
  };

  return (
    <article className="nbs-legal" data-testid="legal-page">
      <header className="nbs-legal__intro">
        <h1 className="nbs-legal__title">{t("legal.privacyTitle")}</h1>
      </header>

      <section className="nbs-legal__block" aria-labelledby="legal-owners-heading">
        <h2 id="legal-owners-heading" className="nbs-legal__heading">
          {t("legal.ownersHeading")}
        </h2>
        <p>
          <Trans i18nKey="legal.ownersBody" components={partnerComponents} />
        </p>
        <p>
          <Trans i18nKey="legal.ownersPolicy" components={partnerComponents} />
        </p>
      </section>

      <section className="nbs-legal__block" aria-labelledby="legal-data-heading">
        <h2 id="legal-data-heading" className="nbs-legal__heading">
          {t("legal.dataHeading")}
        </h2>
        <p>{t("legal.dataBody")}</p>
        <p>{t("legal.dataConsent")}</p>
      </section>

      <section className="nbs-legal__block" aria-labelledby="legal-security-heading">
        <h2 id="legal-security-heading" className="nbs-legal__heading">
          {t("legal.securityHeading")}
        </h2>
        <p>{t("legal.securityBody")}</p>
        <p>
          <Trans i18nKey="legal.rightsBody" components={partnerComponents} />
        </p>
      </section>

      <section className="nbs-legal__block" aria-labelledby="legal-privacy-heading">
        <h2 id="legal-privacy-heading" className="nbs-legal__heading">
          {t("legal.privacyHeading")}
        </h2>
        <p>
          <Trans
            i18nKey="legal.privacyBody"
            values={{ email: CONTACT_EMAIL }}
            components={{
              email: <a className="nbs-legal__mail" href={`mailto:${CONTACT_EMAIL}`} />,
              ...partnerComponents,
            }}
          />
        </p>
      </section>

      <section id="conditions" className="nbs-legal__block" aria-labelledby="legal-terms-heading">
        <h2 id="legal-terms-heading" className="nbs-legal__heading">
          {t("legal.termsTitle")}
        </h2>
        <p>
          <Trans i18nKey="legal.termsBody" components={partnerComponents} />
        </p>
        <p>{t("legal.termsData")}</p>
        <p>
          <Trans
            i18nKey="legal.termsContact"
            values={{ email: CONTACT_EMAIL }}
            components={{
              email: <a className="nbs-legal__mail" href={`mailto:${CONTACT_EMAIL}`} />,
            }}
          />
        </p>
      </section>

      <p className="nbs-legal__back">
        <Link to="/">{t("pages.goHome")}</Link>
      </p>
    </article>
  );
}
